// Clase padre de todos los servicios internos usados en la aplicación
// para heredar métodos comunes como el handleError y el envío de bugs

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
/**
 * Servicio base para servicios internos.
 * Provee manejo de errores y reporte automático de bugs.
 */
@Injectable()
export class InternalService {
  /**
   * Constructor: inyecta dependencias comunes.
   */
  constructor(
    protected http: HttpClient,
    protected router: Router,
    protected authService: AuthService
  ) {}
  /**
   * Maneja errores HTTP y dispara el envío automático de un bug report.
   * @param error Error HTTP recibido.
   * @returns Observable que lanza el error.
   */
  protected handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Error en la solicitud HTTP:', error);
    if (error.status === 401) {
      this.authService.logout();
      // Evita redirigir si la ruta es /auth/complete-registration/
      const currentUrl = this.router.url;
      if (
        currentUrl !== '/login' &&
        !currentUrl.startsWith('/auth/complete-registration')
      ) {
        this.router.navigate(['/login']);
      }
    } else if (error.status !== 400) {
      this.sendBugReport().subscribe({
        next: () => {}
      });
    }
    console.log("error en handler", error)
    return throwError(() => error);
  }

  /**
   * Envía un reporte de bug automático con información del entorno, consola y último error.
   * @returns Observable con la respuesta del backend.
   */
  protected sendBugReport(): Observable<any> {
    // Recopila información del entorno del usuario
    const environmentData = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    };

    // Recopila logs de la consola y limita tamaño/cantidad para evitar errores de backend
    const consoleLogs = (window as any).getConsoleLogs();
    const MAX_FIELD = 4000;
    const MAX_LOGS = 20;
    const MAX_ERRORS = 10;
    const MAX_WARNINGS = 10;
    function truncate(str: string, max: number) {
      return typeof str === 'string' && str.length > max ? str.slice(0, max) + '…[truncated]' : str;
    }
    // Se conservan los objetos crudos de error para poder extraer detalles (endpoint, método,
    // mensaje). El arreglo `errors` ya serializado solo sirve para el campo `data`.
    const rawErrors = consoleLogs.filter((l: any) => l.type === 'error').slice(-MAX_ERRORS);
    const errors = rawErrors.map((l: any) => truncate(JSON.stringify(l), MAX_FIELD));
    const warnings = consoleLogs.filter((l: any) => l.type === 'warn').slice(-MAX_WARNINGS).map((l: any) => truncate(JSON.stringify(l), MAX_FIELD));
    const logs = consoleLogs.filter((l: any) => l.type === 'log').slice(-MAX_LOGS).map((l: any) => truncate(JSON.stringify(l), MAX_FIELD));

    // Serializa la información relevante para el campo data y la trunca
    const dataString = JSON.stringify({
      environment: environmentData,
      logs,
      errors,
      warnings,
    }, null, 2);
    const MAX_DATA_LENGTH = 4000;
    const safeDataString = dataString.length > MAX_DATA_LENGTH
      ? dataString.substring(0, MAX_DATA_LENGTH) + '... [truncated]'
      : dataString;

    // Extrae información relevante del último error para el reporte
    let mensaje = '';
    let descripcion = '';
    let metodo = '';
    if (rawErrors.length > 0) {
      const ultimoError = rawErrors[rawErrors.length - 1];
      let endpoint = '';
      let requestPayload = '';
      if (ultimoError.args && ultimoError.args.length > 1 && typeof ultimoError.args[1] === 'object') {
        endpoint = ultimoError.args[1].url ? `\nEndpoint: ${ultimoError.args[1].url}` : '';
        // Si hay un body enviado en la petición, lo agrega
        if (ultimoError.args[1].error && typeof ultimoError.args[1].error === 'object' && ultimoError.args[1].error.body) {
          requestPayload = `\nPayload: ${JSON.stringify(ultimoError.args[1].error.body, null, 2)}`;
        }
        mensaje = ultimoError.args[1].message || 'Error desconocido';
        // Intenta extraer el método HTTP si está disponible
        metodo = ultimoError.args[1].method ? ultimoError.args[1].method.toUpperCase() : 'OTHER';
        if (typeof ultimoError.args[1].error === 'string') {
          // Extrae el mensaje de <pre class="exception_value"> si existe
          const preMatch = ultimoError.args[1].error.match(/<pre class="exception_value">(.*?)<\/pre>/is);
          if (preMatch && preMatch[1]) {
            descripcion = preMatch[1]
              .replace(/&#x27;/g, "'")
              .replace(/&quot;/g, '"')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&amp;/g, '&')
              .trim();
            mensaje = descripcion.split('\n')[0] || mensaje;
          } else {
            // Si no, intenta extraer el <title>
            const titleMatch = ultimoError.args[1].error.match(/<title>(.*?)<\/title>/i);
            descripcion = titleMatch ? titleMatch[1] : ultimoError.args[1].error.substring(0, 200);
          }
        } else {
          descripcion = JSON.stringify(ultimoError.args[1], null, 2);
        }
      } else {
        mensaje = ultimoError.args ? ultimoError.args[0] : 'Error desconocido';
        descripcion = JSON.stringify(ultimoError, null, 2);
      }
      // Agrega endpoint y payload a la descripción
      descripcion += endpoint + requestPayload;
    } else {
      mensaje = 'Automatic bug report';
      descripcion = 'No error details available';
    }

    // Prepara headers de autenticación
    let token = localStorage.getItem('token');
    if (token) token = JSON.parse(token);
    const authHeader = 'Bearer ' + (token || '');
    const headers = new HttpHeaders()
      .set('Authorization', authHeader)
      .set('Content-Type', 'application/json');

    // Obtiene el id del usuario y, una vez resuelto, envía el reporte. Antes el `reporter`
    // se leía de forma síncrona de una respuesta asíncrona, por lo que siempre era undefined.
    // El GET /user/me/ NO se enruta por handleError para evitar recursión infinita (un fallo
    // de /user/me/ dispararía otro sendBugReport, y así sucesivamente).
    return this.http.get(`${environment.apiUrl}/user/me/`, { headers }).pipe(
      map((userData: any) => (userData && userData.id != null ? userData.id : null)),
      catchError(() => of(null)),
      switchMap((reporterId: any) => {
        // Truncar todos los campos principales del payload
        const bugReportPayload = {
          title: truncate(mensaje, MAX_FIELD),
          description: truncate(descripcion, MAX_FIELD),
          method: truncate(metodo, MAX_FIELD),
          full_stack_trace: '',
          data: truncate(safeDataString, MAX_FIELD),
          status: 'open',
          priority: 'medium',
          assigned_to: undefined,
          reporter: reporterId,
        };

        // Envía el reporte al backend
        return this.http.post(
          `${environment.apiUrl}/bug-reports/post-bug-report/`,
          bugReportPayload,
          { headers }
        );
      })
    );
  }
}